import React, { useState } from "react";
import { Redirect, useHistory } from "react-router";
import { agent } from "../agent";
import PaymentMakePage from "../pages/PaymentMake";
import { URL_PATHS } from "../routes";
import { PaymentType } from "../generated/lib-domain/paymentZeus";
import {
    IAPPaymentMethod,
    OtherPaymentMethod,
    PaymentMethod
} from "../generated/lib-domain/iap";
import {
    PaymentMethodEnum,
    Prefecture,
    SubscriptionEnum,
    UserAddress
} from "../generated/UserAPIAgent";
import { tokenZeus } from "../lib/zeus";
import { useDispatch, useSelector } from "react-redux";
import { myDataActions } from "../store/myData";
import { paymentActions } from "../store/payment";
import { AppState } from "../../src/store";
import { UserStatus } from "../generated/lib-domain/user";
import { PAYMENT_PLANS } from "../../../lib-domain/dist/lib/paymentPlan";
import { Message } from "../types/common";
import { useOnceCall } from "../custom-hooks/once-call";
import { smartAlert } from "../lib/confirm-alert";

interface PaymentMakeState {
    paymentMakeIsLoading: boolean;
    paymentMakeIsFinished: boolean;
    registerAddressIsLoading: boolean;
    userAddress: UserAddress | null;
    // 過去に有料登録したことがある現無料会員
    isRepeatFreeUser: boolean;
}
const initialState: PaymentMakeState = {
    paymentMakeIsLoading: false,
    paymentMakeIsFinished: false,
    registerAddressIsLoading: false,
    userAddress: null,
    isRepeatFreeUser: false
};

const PaymentMakeContainer = () => {
    const [paymentMake, setPaymentMake] = useState(initialState);
    const payment = useSelector((state: AppState) => state.payment);
    const user = useSelector((state: AppState) => state.myData.user);
    const dispatch = useDispatch();
    const history = useHistory();
    useOnceCall(() => {
        (async () => {
            try {
                const [paymentStatus, address] = await Promise.all([
                    agent.getPaymentStatus(),
                    user.status === UserStatus.Created
                        ? null
                        : agent.getUserAddress()
                ]);
                setPaymentMake({
                    ...paymentMake,
                    userAddress: address || null,
                    isRepeatFreeUser:
                        paymentStatus.paymentType === PaymentType.Repeat &&
                        paymentStatus.nextPaymentDate == null &&
                        paymentStatus.paidFinishDate == null
                });
            } catch (e) {
                console.error(e);
                await smartAlert("決済情報の取得に失敗しました。");
            }
        })();
    });
    /**
     * カード決済中有料会員のサブスクリプション変更か判断
     */
    const isChangeCardPlanPaidUser = (): boolean => {
        if (
            !payment.status?.currentPaymentPlan ||
            !payment.selectedPayment ||
            payment.status?.primaryPaymentMethod !==
                OtherPaymentMethod.CREDIT_CARD
        ) {
            return false;
        }
        const selectedSubscription: SubscriptionEnum = PAYMENT_PLANS.find(
            (plan) => plan.id === payment.selectedPayment.paymentPlanId
        ).subscription;
        return (
            payment.status.currentPaymentPlan.subscription !==
            selectedSubscription
        );
    };
    async function onSubmitAddress(
        zip: string,
        pref: Prefecture,
        city: string,
        address: string,
        building?: string
    ): Promise<void> {
        setPaymentMake({ ...paymentMake, registerAddressIsLoading: true });
        try {
            await agent.registerPaymentAddress({
                zip,
                pref,
                city,
                address,
                building
            });
            await agent.postPaymentZeusSubscribe({
                currentPaymentState: {
                    paymentType: payment.status.paymentType as PaymentType,
                    paymentPlanId: payment.status.currentPaymentPlan
                        ? payment.status.currentPaymentPlan.id
                        : null,
                    paymentMethod:
                        (payment.status
                            .primaryPaymentMethod as PaymentMethodEnum) || null
                },
                newPayment: payment.selectedPayment
            });
            dispatch(myDataActions.myDataShouldLoadSet(true)); // 終わったところでユーザー情報を再fetchする
            setPaymentMake({ ...paymentMake, registerAddressIsLoading: false });
            history.push(URL_PATHS.paymentMakeFinish());
        } catch (error) {
            setPaymentMake({ ...paymentMake, registerAddressIsLoading: false });
            await smartAlert("有料会員情報登録に失敗しました。");
        }
    }
    async function onSubmitCreditCardInfo(creditCard: {
        card: string;
        month: number;
        year: number;
        csc: string;
    }): Promise<void> {
        setPaymentMake({ ...paymentMake, paymentMakeIsLoading: true });
        try {
            const res = await tokenZeus({
                cardNumber: creditCard.card,
                expiresMonth: creditCard.month.toString().padStart(2, "0"),
                expiresYear: creditCard.year.toString(),
                securityNo: creditCard.csc
            });
            if (res.status === "invalid") {
                throw Error("invalid credit card");
            }

            if (user.status === UserStatus.Created) {
                // Webから会員登録して有料登録を行うケース
                await agent.putPaymentCreditCardZeus({
                    tokenKey: res.tokenKey
                });
                await agent.postPaymentZeusSubscribe({
                    currentPaymentState: {
                        paymentType: payment.status.paymentType as PaymentType,
                        paymentPlanId: null,
                        paymentMethod: null
                    },
                    newPayment: payment.selectedPayment
                });
                dispatch(myDataActions.myDataShouldLoadSet(true));
                setPaymentMake({ ...paymentMake, paymentMakeIsLoading: false });
                history.push(URL_PATHS.installApp());
                return;
            }

            if (payment.isChangeCardInfoInProgress) {
                // クレジットカード情報の変更
                await agent.putPaymentCreditCardZeus({
                    tokenKey: res.tokenKey
                });
                dispatch(myDataActions.myDataShouldLoadSet(true));
                setPaymentMake({ ...paymentMake, paymentMakeIsLoading: false });
                history.push(URL_PATHS.creditCardInfoChangeFinish());
                return;
            }

            if (paymentMake.isRepeatFreeUser || isChangeCardPlanPaidUser()) {
                // 過去に有料登録したことある現無料会員 or サブスクリプション変更する有料会員
                await agent.putPaymentCreditCardZeus({
                    tokenKey: res.tokenKey
                });
                await agent.postPaymentZeusSubscribe({
                    currentPaymentState: {
                        paymentType: payment.status.paymentType as PaymentType,
                        paymentPlanId:
                            payment.status.currentPaymentPlan?.id || null,
                        paymentMethod: payment.status
                            .primaryPaymentMethod as PaymentMethodEnum
                    },
                    newPayment: payment.selectedPayment
                });
                dispatch(myDataActions.myDataShouldLoadSet(true));
                setPaymentMake({ ...paymentMake, paymentMakeIsLoading: false });
                if (paymentMake.isRepeatFreeUser) {
                    history.push(URL_PATHS.paymentMakeFinish());
                } else {
                    history.push(URL_PATHS.subscriptionChangeFinish());
                }
                return;
            }

            if (payment.status.paymentType === PaymentType.First) {
                // アプリから登録した無料会員
                await agent.putPaymentCreditCardZeus({
                    tokenKey: res.tokenKey
                });
                setPaymentMake({
                    ...paymentMake,
                    paymentMakeIsFinished: true,
                    paymentMakeIsLoading: false
                });
                return; // 初回決済の場合はstateを変えて住所入力へ
            }

            if (
                payment.status.primaryPaymentMethod === IAPPaymentMethod.IOS_IAP
            ) {
                // IAP決済からクレジットカード決済への変更
                dispatch(paymentActions.paymentTokenSet(res.tokenKey));
                setPaymentMake({ ...paymentMake, paymentMakeIsLoading: false });
                history.push(URL_PATHS.confirmTransition());
                return;
            }
        } catch (error) {
            console.error(error);
            setPaymentMake({ ...paymentMake, paymentMakeIsLoading: false });
            await smartAlert("クレジットカードの登録処理に失敗しました。");
        }
    }

    if (!payment.inProgress) {
        return <Redirect to={URL_PATHS.paymentInfo()} />;
    }
    const campaigns = payment.status.cardPaymentPlans.find(
        (plan) => plan.paymentPlanId === payment.selectedPayment?.paymentPlanId
    )?.campaigns;
    const campaignMessage: Message | null =
        campaigns &&
        campaigns.length > 0 &&
        (campaigns[0].title !== "" || campaigns[0].description !== "")
            ? {
                  title: campaigns[0].title,
                  description: campaigns[0].description
              }
            : null;
    return (
        <PaymentMakePage
            onSubmitCreditCardInfo={onSubmitCreditCardInfo}
            onSubmitAddress={onSubmitAddress}
            isChangeCreditCardInfo={payment.isChangeCardInfoInProgress}
            paymentMakeIsLoading={paymentMake.paymentMakeIsLoading}
            paymentMakeIsFinished={paymentMake.paymentMakeIsFinished}
            registerAddressIsLoading={paymentMake.registerAddressIsLoading}
            paymentInfoState={{
                paymentType: payment.status.paymentType,
                orderedAmount:
                    payment.selectedPayment?.firstAmount ||
                    payment.selectedPayment?.amount,
                primaryPaymentMethod: payment.status
                    .primaryPaymentMethod as PaymentMethod,
                cardPaymentPlan: payment.status.cardPaymentPlans.find(
                    (plan) =>
                        plan.paymentPlanId ===
                        payment.selectedPayment?.paymentPlanId
                )
            }}
            userAddress={paymentMake.userAddress}
            userStatus={user.status}
            campaignMessage={campaignMessage}
        />
    );
};

export default PaymentMakeContainer;
